/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import { Modal, Row, Col, Button } from "@duxton-web/ui"
import { CheckmarkUniversal, WarningAltFilled } from "@duxton-web/icons"
import { BulkUploadTask } from "types/delv-platform-api/bulkUploadTask"
import { useTranslations } from "common/utils/i18n"
import _toString from "lodash/toString"
import { useScribeBulkUploadNotifications } from "pages/menu/detail/bulkUpload/scribe/useScribeBulkUploadNotifications"
import { useTranslateSingularAndPlural } from "hooks/useTranslateSingularAndPlural"

type PropTypes = {
  visible: boolean
  completedBulkUploadTask?: BulkUploadTask
  onCloseCallback: () => void
}

const modalWrapCss = css`
  .dui-modal-content {
    padding: 0;
  }
`

const rowWrap = css`
  display: flex;
  align-items: center;
  padding: 28px 24px;
`

const rowWrapHidden = css`
  display: none;
`

const successIconWrapCss = css`
  background-color: #33c072;
  width: 45px;
  height: 45px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  .duxicon {
    color: #ffffff;
    font-size: 28px;
  }
`

const primaryTextCss = css`
  font-size: 20px;
  font-weight: 500;
  color: #000000;
`

const descriptionTextCss = css`
  font-size: 18px;
  color: #707070;
`

const downloadCSVBtnCss = css`
  min-width: 160px;
  height: 45px;
  font-size: 16px;
  font-weight: 500;
  background-color: #005339;
`

const UploadSuccessWithError = (props: PropTypes) => {
  const { visible, completedBulkUploadTask, onCloseCallback } = props
  const isErrorReportAvailable = !!completedBulkUploadTask?.errorReportURL
  const { trackDownloadCSVClick } = useScribeBulkUploadNotifications()
  const successRowCount = completedBulkUploadTask?.successRowCount || 0
  const errorRowCount = completedBulkUploadTask?.errorRowCount || 0
  const totalRowCount = completedBulkUploadTask?.totalRowCount || 0

  const [
    successNotiText,
    errorDescNotiText,
    errorNoItemCountNotiText,
    errorNoItemCountDescNotiText,
    downloadReportButtonText,
  ] = useTranslations([
    [
      "bulkUpload.notifications.partialSuccessWithItemCount.successTitle",
      { "<x>": _toString(successRowCount), "<y>": _toString(totalRowCount) },
    ],
    "bulkUpload.notifications.partialSuccessWithItemCount.failedDescription",
    "bulkUpload.notifications.partialSuccessNoItemCount.title",
    "bulkUpload.notifications.partialSuccessNoItemCount.description",
    "bulkUpload.notifications.partialSuccessWithItemCount.downloadFileButton",
  ])
  const errorNotiText = useTranslateSingularAndPlural(
    "bulkUpload.notifications.partialSuccessWithItemCount.failedTitle",
    errorRowCount,
    { x: _toString(errorRowCount) },
  )

  const onClickDownloadErrorReport = () => {
    if (completedBulkUploadTask?.errorReportURL) {
      trackDownloadCSVClick(completedBulkUploadTask.taskID)
      window.location.assign(completedBulkUploadTask.errorReportURL)
    }
  }

  return (
    <Modal
      open={visible}
      footer={null}
      css={modalWrapCss}
      width={600}
      onCancel={onCloseCallback}
      maskClosable={false}
      closable
    >
      <div
        css={css`
          ${rowWrap};
          border-bottom: 1px solid #d9d9d9;
          ${successRowCount ? "" : rowWrapHidden}
        `}
      >
        <div>
          <div
            css={css`
              ${successIconWrapCss};
              margin-right: 16px;
            `}
          >
            <CheckmarkUniversal />
          </div>
        </div>
        <div>
          <div css={primaryTextCss}>{successNotiText}</div>
        </div>
      </div>
      {/* Antd Grid flex will break (line break) if the text inside exceed its maximum width able to be flex, hence use div and css */}
      <div
        css={css`
          ${rowWrap};
          align-items: flex-start;
        `}
      >
        <div
          css={css`
            margin-right: 16px;
            .duxicon {
              color: #d42e1c;
              font-size: 54px;
            }
          `}
        >
          <WarningAltFilled />
        </div>
        <div>
          <div css={primaryTextCss}>
            {errorRowCount ? errorNotiText : errorNoItemCountNotiText}
          </div>
          <div
            css={css`
              ${descriptionTextCss};
              margin-top: 8px;
            `}
          >
            {errorRowCount ? errorDescNotiText : errorNoItemCountDescNotiText}
          </div>
          {isErrorReportAvailable && (
            <Row
              align="middle"
              gutter={16}
              css={css`
                margin-top: 14px;
              `}
            >
              <Col>
                <Button
                  block
                  css={downloadCSVBtnCss}
                  type="primary"
                  onClick={onClickDownloadErrorReport}
                >
                  {downloadReportButtonText}
                </Button>
              </Col>
            </Row>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default UploadSuccessWithError
